import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory, useParams } from 'react-router-dom'
import {
  Button,
  Checkbox,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Chip,
  TextField,
  IconButton,
  Collapse,
  Grid
} from '@material-ui/core'
import {
  ExpandMore,
  ExpandLess
} from '@material-ui/icons'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const SOURCE_TYPE_STYLE = {
  plans: { backgroundColor: '#bbdefb', color: '#0d47a1' },
  bid_response: { backgroundColor: '#e1bee7', color: '#4a148c' },
  manual: { backgroundColor: '#e0e0e0', color: '#424242' }
}

const MaterialsRollupView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [project, setProject] = useState(null)
  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterText, setFilterText] = useState('')
  const [expandedKey, setExpandedKey] = useState(null)
  const [selectedKeys, setSelectedKeys] = useState([])
  const [creatingSubmittal, setCreatingSubmittal] = useState(false)
  const [submittalError, setSubmittalError] = useState('')

  useEffect(() => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}`)
      .then(res => setProject(res.data))
      .catch(() => {})

    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/materials-rollup`)
      .then(res => {
        setMaterials(res.data || [])
        setLoading(false)
      })
      .catch(err => {
        console.error('Materials rollup failed:', err)
        setLoading(false)
      })
  }, [projectId, props.url])

  useEffect(() => {
    setSelectedKeys(prev => {
      const available = new Set(materials.map(m => m.key))
      return prev.filter(key => available.has(key))
    })
  }, [materials])

  const filtered = useMemo(() => {
    if (!filterText.trim()) return materials
    const q = filterText.toLowerCase()
    return materials.filter(m =>
      (m.description || '').toLowerCase().includes(q) ||
      (m.catalogDescription || '').toLowerCase().includes(q) ||
      (m.category || '').toLowerCase().includes(q)
    )
  }, [materials, filterText])

  const totals = useMemo(() => ({
    items: filtered.length,
    totalCost: filtered.reduce((s, m) => s + (m.totalCost || 0), 0),
    unpriced: filtered.filter(m => !m.totalCost && !m.unitCost && !m.bestPrice).length
  }), [filtered])

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys])
  const filteredKeys = useMemo(() => filtered.map(m => m.key), [filtered])
  const filteredSelectedCount = useMemo(() => filteredKeys.filter(key => selectedKeySet.has(key)).length, [filteredKeys, selectedKeySet])
  const allFilteredSelected = filteredKeys.length > 0 && filteredKeys.every(key => selectedKeySet.has(key))

  const toggleMaterial = (key) => {
    setSubmittalError('')
    setSelectedKeys(prev => prev.includes(key)
      ? prev.filter(itemKey => itemKey !== key)
      : [...prev, key])
  }

  const toggleFilteredMaterials = () => {
    setSubmittalError('')
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        filteredKeys.forEach(key => next.delete(key))
      } else {
        filteredKeys.forEach(key => next.add(key))
      }
      return Array.from(next)
    })
  }

  const createSubmittalPacket = async () => {
    if (selectedKeys.length === 0) return

    setCreatingSubmittal(true)
    setSubmittalError('')
    try {
      const { data } = await axios.post(`${props.url}/api/bid-estimation/projects/${projectId}/submittals/from-materials`, {
        materialKeys: selectedKeys
      })
      history.push(`/bid-estimation/${projectId}/submittals/${data.submittal._id}`)
    } catch (err) {
      setSubmittalError(err.response?.data?.error || 'Failed to create submittal packet')
    } finally {
      setCreatingSubmittal(false)
    }
  }

  const getCoverageChip = (item) => {
    if (item.unitCost > 0 || item.bestPrice > 0) {
      return { label: 'Priced', style: { backgroundColor: '#c8e6c9', color: '#2e7d32' } }
    }
    if (item.totalCost > 0) {
      return { label: 'No Unit Cost', style: { backgroundColor: '#fff3e0', color: '#e65100' } }
    }
    return { label: 'Unpriced', style: { backgroundColor: '#ffcdd2', color: '#c62828' } }
  }

  return (
    <div style={{ padding: 20 }}>
      <BidEstimationHeader
        backTo={`/bid-estimation/${projectId}`}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Materials' }
        ]}
        title={`Materials${project?.name ? ' · ' + project.name : ''}`}
      />

      {/* Filter bar */}
      <Paper style={{ padding: 12, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="Search materials..."
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
          </Grid>
          <Grid item>
            <Chip size="small" label={`${totals.items} items`} />
          </Grid>
          <Grid item>
            <Chip size="small" label={`${selectedKeys.length} selected`} />
          </Grid>
          <Grid item>
            <Chip size="small" label={`Total: ${fmt(totals.totalCost)}`}
              style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
          </Grid>
          {totals.unpriced > 0 && (
            <Grid item>
              <Chip size="small" label={`${totals.unpriced} unpriced`}
                style={{ backgroundColor: '#ffcdd2', color: '#c62828' }} />
            </Grid>
          )}
          <Grid item style={{ marginLeft: 'auto' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={toggleFilteredMaterials}
              disabled={filtered.length === 0}
              style={{ marginRight: 8 }}
            >
              {allFilteredSelected ? 'Clear Filtered' : 'Select Filtered'}
            </Button>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={createSubmittalPacket}
              disabled={selectedKeys.length === 0 || creatingSubmittal}
            >
              {creatingSubmittal ? 'Generating...' : 'Generate Submittal'}
            </Button>
          </Grid>
        </Grid>
        {submittalError && (
          <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
            {submittalError}
          </Typography>
        )}
      </Paper>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      ) : filtered.length === 0 ? (
        <Paper style={{ padding: 40, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            {materials.length === 0
              ? 'No material components found. Add components to line items to build the materials list.'
              : 'No materials match the current filter.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={allFilteredSelected}
                    indeterminate={filteredSelectedCount > 0 && !allFilteredSelected}
                    onChange={toggleFilteredMaterials}
                    inputProps={{ 'aria-label': 'Select filtered materials' }}
                  />
                </TableCell>
                <TableCell style={{ width: 40 }} />
                <TableCell>Description</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Total Qty</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
                <TableCell align="right">Best Price</TableCell>
                <TableCell align="right">Total Cost</TableCell>
                <TableCell align="center">Coverage</TableCell>
                <TableCell align="center">Sources</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(item => {
                const isExpanded = expandedKey === item.key
                const coverage = getCoverageChip(item)
                const effectiveUnitCost = item.unitCost || item.bestPrice || 0

                return (
                  <React.Fragment key={item.key}>
                    <TableRow hover style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedKey(isExpanded ? null : item.key)}>
                      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          color="primary"
                          checked={selectedKeySet.has(item.key)}
                          onChange={() => toggleMaterial(item.key)}
                          inputProps={{ 'aria-label': `Select ${item.catalogDescription || item.description}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {item.catalogDescription || item.description}
                        </Typography>
                        {item.catalogItemId && item.description !== item.catalogDescription && (
                          <Typography variant="caption" color="textSecondary">
                            {item.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.category && (
                          <Chip label={item.category} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell align="right">{(item.totalQty || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{effectiveUnitCost ? fmt(effectiveUnitCost) : '—'}</TableCell>
                      <TableCell align="right">
                        {item.bestPrice ? fmt(item.bestPrice) : '—'}
                      </TableCell>
                      <TableCell align="right">{fmt(item.totalCost)}</TableCell>
                      <TableCell align="center">
                        <Chip label={coverage.label} size="small" style={coverage.style} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.sources?.length || 0} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>

                    {/* Expanded sources */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={11} style={{ padding: 0, backgroundColor: '#fafafa' }}>
                          <Collapse in={isExpanded}>
                            <div style={{ padding: '8px 24px 16px 64px' }}>
                              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                Contributing Line Items
                              </Typography>
                              <Table size="small" style={{ marginTop: 4 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Building</TableCell>
                                    <TableCell>Section</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Qty</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(item.sources || []).map((src, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{src.description}</TableCell>
                                      <TableCell>{src.building}</TableCell>
                                      <TableCell>{src.section || '—'}</TableCell>
                                      <TableCell>
                                        <Chip
                                          label={src.sourceType || 'manual'}
                                          size="small"
                                          style={{
                                            ...(SOURCE_TYPE_STYLE[src.sourceType] || SOURCE_TYPE_STYLE.manual),
                                            height: 20,
                                            fontSize: 11
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{(src.componentQty || 0).toLocaleString()}</TableCell>
                                      <TableCell align="right">{fmt(src.extendedCost)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(MaterialsRollupView)
